import { BLUE_600, BOX_SHADOW_XS, CORAL_600, GRAY_300, GRAY_700, GRAY_800, GRAY_900, GREEN_600, WHITE, YELLOW_700 } from 'constants/styling/theme'
import { ConversationSummaryDTO, LastMessageSenderKind } from 'models/conversations'
import { OverviewGroup, OverviewMode, buildCardMeta, getCategoryLabelKey, getLastSenderName, resolveLastSenderKind } from '../../helpers/conversationOverview'

import Box from '@mui/material/Box'
import { FC } from 'react'
import { OverviewStatusPill } from './OverviewStatusPill'
import Stack from '@mui/material/Stack'
import { TypingDots } from '../../TypingDots/TypingDots'
import Typography from '@mui/material/Typography'
import { getConversationDisplayTitle } from '../../helpers/conversationDisplay'
import { getRelativeTimePart } from '../../helpers/relativeTime'
import { lastMessagePreviewText } from '../../helpers/chatAttachments'
import { useChatWidget } from '../../ChatWidget.context'
import { useTranslation } from 'react-i18next'

const REPLY_NEEDED_BORDER = '#FFC9BC'

interface Props {
  conversation: ConversationSummaryDTO
  currentUserId?: string
  group: OverviewGroup
  mode: OverviewMode
  isSelected: boolean
  onClick: () => void
}

const SENDER_LABEL_KEY: Partial<Record<LastMessageSenderKind, string>> = {
  [LastMessageSenderKind.YOU]: 'chat:overview.sender_you',
  [LastMessageSenderKind.ASSISTANT]: 'chat:overview.sender_assistant',
  [LastMessageSenderKind.SUPPORT]: 'chat:overview.sender_support',
  [LastMessageSenderKind.CREATIVE]: 'chat:role.creative',
  [LastMessageSenderKind.COLLEAGUE]: 'chat:role.client',
}

const NAMED_SENDERS = new Set([LastMessageSenderKind.CREATIVE, LastMessageSenderKind.COLLEAGUE])

export const OverviewConversationCard: FC<Props> = ({ conversation, currentUserId, group, mode, isSelected, onClick }) => {
  const { t, i18n } = useTranslation(['chat'])
  const { typingConversationIds } = useChatWidget()
  const isTyping = typingConversationIds.has(conversation.id)
  const isAssistantOnly = group === OverviewGroup.OTHER
  const unreadCount = conversation.unreadCount ?? 0

  const title = getConversationDisplayTitle(conversation, currentUserId, t)
  const preview = lastMessagePreviewText(conversation, t)
  const categoryLabelKey = getCategoryLabelKey(conversation)
  const meta = buildCardMeta(conversation, mode, categoryLabelKey ? t(categoryLabelKey) : undefined)

  const senderKind = resolveLastSenderKind(conversation, currentUserId)
  const senderName = senderKind && NAMED_SENDERS.has(senderKind) ? getLastSenderName(conversation) : undefined
  const senderLabel = senderName ?? (senderKind && SENDER_LABEL_KEY[senderKind] ? t(SENDER_LABEL_KEY[senderKind] as string) : undefined)

  const timestamp = (() => {
    const at = conversation.lastMessageAt ?? conversation.updatedAt
    if (!at) return null
    const part = getRelativeTimePart(new Date(at))
    switch (part.kind) {
      case 'just_now': return t('chat:time.just_now')
      case 'minutes': return t('chat:time.minutes_ago', { count: part.value })
      case 'hours': return t('chat:time.hours_ago', { count: part.value })
      case 'yesterday': return t('chat:time.yesterday')
      case 'date': return new Intl.DateTimeFormat(i18n.language, { month: 'short', day: 'numeric' }).format(part.date)
    }
  })()

  const borderColor = isSelected
    ? YELLOW_700
    : group === OverviewGroup.REPLY_NEEDED ? REPLY_NEEDED_BORDER : GRAY_300

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-current={isSelected || undefined}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: isAssistantOnly ? '0.4rem' : '0.6rem',
        padding: isAssistantOnly ? '1.2rem 1.6rem' : '1.4rem 1.6rem',
        borderRadius: '1.2rem',
        border: `1px solid ${borderColor}`,
        backgroundColor: WHITE,
        boxShadow: isAssistantOnly ? 'none' : BOX_SHADOW_XS,
        opacity: group === OverviewGroup.RESOLVED ? 0.85 : 1,
        font: 'inherit',
        textAlign: 'left',
        cursor: 'pointer',
        '&:focus-visible': { outline: `2px solid ${BLUE_600}`, outlineOffset: '2px' },
      }}
    >

      <Stack direction="row" alignItems="center" gap="0.8rem" width="100%">

        {unreadCount > 0 &&
          <Box
            role="img"
            aria-label={t('chat:conversation_item.unread', { count: unreadCount })}
            sx={{ width: '0.8rem', height: '0.8rem', borderRadius: '50%', backgroundColor: CORAL_600, flexShrink: 0 }}
          />
        }

        <Typography variant="text-md" fontWeight={600} color={GRAY_900} noWrap sx={{ flex: 1, minWidth: 0, fontSize: '1.5rem' }}>
          {title}
        </Typography>

        <Typography variant="text-xs" color={GRAY_700} noWrap flexShrink={0}>
          {timestamp}
        </Typography>

      </Stack>

      {!!meta &&
        <Typography variant="text-xs" color={GRAY_700} noWrap width="100%">
          {meta}
        </Typography>
      }

      {isTyping &&
        <Stack direction="row" alignItems="center" gap="0.6rem">

          <TypingDots color={GREEN_600} size="0.5rem" />

          <Typography variant="text-sm" sx={{ color: GREEN_600, fontStyle: 'italic', fontSize: '1.3rem' }}>
            {t('chat:thread.typing')}
          </Typography>

        </Stack>
      }

      {!isTyping && !!preview &&
        <Typography
          variant="text-sm"
          color={GRAY_800}
          width="100%"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: isAssistantOnly ? 1 : 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            fontSize: '1.3rem',
            lineHeight: 1.45,
          }}
        >
          {!!senderLabel &&
            <Box component="span" sx={{ fontWeight: 600, color: GRAY_900 }}>
              {`${senderLabel}: `}
            </Box>
          }
          {preview}
        </Typography>
      }

      {group !== OverviewGroup.OTHER &&
        <Box paddingTop="0.2rem">
          <OverviewStatusPill conversationId={conversation.id} group={group} />
        </Box>
      }

    </Box>
  )
}
